/**
 * Los tokens de «la casa del proyecto» — tal como los entregó Claude Design (4-sep-2026).
 *
 * Mau, textual: *«realízalo en dark mode como lo mandó Claude Design»* y *«todo tómalo de
 * ahí: formas, diseño, colores, tokens, tipografía»*.
 *
 * 🔴 SE COPIAN, NO SE INTERPRETAN. Los valores son literales del handoff
 *    (`design_handoff_casa_proyecto_pagos/casa-data.jsx`, función `casaTema`). Ajustar «un
 *    poquito» un color aquí es cómo un sistema de diseño se convierte en 40 tonos de gris.
 *
 * ⚠️ CHOCA A PROPÓSITO CON `UX_REFERENCIAS/DESIGN_SYSTEM.md`, que dice que el oscuro es
 *    sólo para M11 y con otra paleta (`#0F172A`). Ese documento es de antes; para esta
 *    pieza Mau dijo que manda el handoff. No se cambió el documento viejo —no es de esta
 *    pieza decidirlo— pero queda dicho aquí para que nadie crea que fue descuido.
 *
 * El handoff pide además: **Inter** para el texto y **JetBrains Mono para TODAS las cifras**,
 * con `tabular-nums` y letter-spacing negativo en las grandes. Las dos ya se cargan en
 * `index.html`.
 */

import { createContext, useContext, useEffect, useState } from 'react';

export interface CasaTema {
  dark: boolean;
  bg: string;
  panel: string; panelSolid: string; panelSoft: string;
  raise: string;
  line: string; lineSoft: string;
  fg: string; fg2: string; fg3: string;
  accent: string; accentSoft: string; accentLine: string;
  danger: string; dangerSoft: string; dangerLine: string;
  /**
   * Ronda 3 (re-revisión 2026-09-10, 🟡 «4 colores a mano») — el texto sobre un botón
   * SÓLIDO de `danger` (Rechazar/Cancelar y devolver, Autorizar y avisar en bypass). El
   * handoff (`casa-estados.jsx:149`) lo trae hardcodeado a `'#fff'` en las DOS paletas —
   * no varía por modo, a diferencia de `btnFg` — así que se declara aquí en vez de repetir
   * el literal en cada botón. Opcional para no romper temas construidos a mano en otras
   * pruebas (`SeccionResumen-salud.test.tsx`) que no lo declaran; los consumidores caen a
   * `'#fff'`, el mismo valor que ya tenían.
   */
  dangerFg?: string;
  /**
   * D-2026-09-30-E (Mau, 30-sep) — el RELLENO del botón rojo SÓLIDO. En claro es el mismo `danger`;
   * en oscuro se oscurece a #DC2626 para que la letra blanca (`dangerFg`) llegue a ≥4.5 de contraste
   * (con `danger` #F87171 medía 2.77). Sólo lo usa ese botón: avisos, badges y peligro suave siguen en `danger`.
   */
  dangerSolid?: string;
  warn: string; warnSoft: string;
  chip: string; input: string; inputLine: string;
  btnFg: string; btnBg: string;
  shadow: string;
  estados: {
    sinMandar: string; validacion: string; autorizacion: string;
    cfo: string; listo: string; pagado: string; rechazado: string;
  };
  /**
   * I-242: el tinte y el borde del acento «informativo» (`estados.cfo`, D-2026-09-30-G) y el borde ámbar (`estados.validacion`).
   * Antes cada pantalla armaba el suyo concatenando dos dígitos al hex del token (`${T.estados.cfo}1F`): hex de 8 dígitos
   * escrito a mano que el candado no veía y que se cae si el token pasa a `rgba(...)`. Un solo lugar, claro y oscuro.
   */
  cfoSoft: string; cfoLine: string; validacionLine: string;
  /** BUG-4265: tinta de aviso ROJO: texto sobre `dangerSoft`/panel. Claro 5.8:1 (5.3:1 sobre el fondo de la casa; `danger` mide 4.35) y oscuro 5.2:1 en el peor fondo. */
  dangerInk: string;
  /** Tinta de aviso ámbar: texto sobre `warnSoft`/panel. Claro 5.5:1 y oscuro 7.9:1 en el peor fondo (BUG-3854; `warn` en claro mide 3.9:1 ahí). */
  warnInk: string;
}

/** La familia mono del handoff. Va en toda cifra, código de partida y fecha corta. */
export const MONO = "'JetBrains Mono', ui-monospace, monospace";
export const SANS = "'Inter', system-ui, sans-serif";

const OSCURO: CasaTema = {
  dark: true,
  bg: 'radial-gradient(140% 100% at 0% 0%, #1A1E22 0%, #131619 45%, #0F1214 100%)',
  panel: 'rgba(255,255,255,0.045)', panelSolid: '#191D21', panelSoft: 'rgba(255,255,255,0.028)',
  raise: 'rgba(255,255,255,0.07)',
  line: 'rgba(255,255,255,0.09)', lineSoft: 'rgba(255,255,255,0.055)',
  fg: '#F2F4F3', fg2: '#A9B0B6',
  // BUG-2498 (decisión «2 A» de Mau, 30-sep 15:46, RELATADA por el orquestador): el fg3 del oscuro era #6E767D = 3.67:1 sobre el
  // panel #191D21; #7F878E llega a 4.65:1. El claro NO cambia (su #9CA3AF queda en ficha aparte).
  fg3: '#7F878E',
  accent: '#34D399', accentSoft: 'rgba(52,211,153,0.14)', accentLine: 'rgba(52,211,153,0.30)',
  danger: '#F87171', dangerSoft: 'rgba(248,113,113,0.12)', dangerLine: 'rgba(248,113,113,0.28)',
  dangerFg: '#FFFFFF', dangerSolid: '#DC2626',
  warn: '#FBBF24', warnSoft: 'rgba(251,191,36,0.12)',
  chip: 'rgba(255,255,255,0.06)', input: 'rgba(255,255,255,0.05)', inputLine: 'rgba(255,255,255,0.12)',
  btnFg: '#0F1419', btnBg: '#F2F4F3',
  shadow: '0 1px 2px rgba(0,0,0,0.4), 0 12px 32px -12px rgba(0,0,0,0.6)',
  estados: { sinMandar: '#8E969D', validacion: '#FBBF24', autorizacion: '#F59E0B', cfo: '#A5B4FC', listo: '#5EEAD4', pagado: '#34D399', rechazado: '#F87171' },
  cfoSoft: 'rgba(165,180,252,0.14)', cfoLine: 'rgba(165,180,252,0.30)', validacionLine: 'rgba(251,191,36,0.40)', warnInk: '#FBBF24',
  dangerInk: '#F87171', // revision-previa-ok: casaTema es el dueño de los colores del tema
};

const CLARO: CasaTema = {
  dark: false,
  bg: 'radial-gradient(140% 100% at 0% 0%, #ECECE5 0%, #F4F3EE 45%, #EFEEE8 100%)',
  panel: 'rgba(255,255,255,0.82)', panelSolid: '#FFFFFF', panelSoft: 'rgba(15,20,25,0.025)',
  raise: 'rgba(255,255,255,0.95)',
  line: 'rgba(15,20,25,0.09)', lineSoft: 'rgba(15,20,25,0.05)',
  fg: '#0F1419', fg2: '#4B5563', fg3: '#9CA3AF',
  accent: '#0F766E', accentSoft: 'rgba(15,118,110,0.09)', accentLine: 'rgba(15,118,110,0.22)',
  danger: '#DC2626', dangerSoft: 'rgba(220,38,38,0.07)', dangerLine: 'rgba(220,38,38,0.20)',
  dangerFg: '#FFFFFF', dangerSolid: '#DC2626',
  warn: '#B45309', warnSoft: 'rgba(180,83,9,0.08)',
  chip: 'rgba(15,20,25,0.05)', input: '#FFFFFF', inputLine: 'rgba(15,20,25,0.14)',
  btnFg: '#FFFFFF', btnBg: '#0F1419',
  shadow: '0 1px 2px rgba(15,20,25,0.04), 0 4px 16px -4px rgba(15,20,25,0.10)',
  estados: { sinMandar: '#9CA3AF', validacion: '#D97706', autorizacion: '#B45309', cfo: '#4F46E5', listo: '#0F766E', pagado: '#10B981', rechazado: '#DC2626' },
  cfoSoft: 'rgba(79,70,229,0.08)', cfoLine: 'rgba(79,70,229,0.22)', validacionLine: 'rgba(217,119,6,0.40)', warnInk: '#92400E',
  dangerInk: '#B91C1C', // revision-previa-ok: casaTema es el dueño de los colores del tema
};

export const casaTema = (dark: boolean): CasaTema => (dark ? OSCURO : CLARO);

/**
 * Un color de DATO (el de un estado de unidad, el acento de una tarjeta) con transparencia. Antes cada pantalla le pegaba
 * dos dígitos hexadecimales al color: un hex de 8 dígitos escrito a mano que se rompe si el color llega con transparencia o
 * como variable. `color-mix` acepta cualquier color; `porcentaje` es la opacidad (13 ≈ el 22 hexadecimal).
 */
export const conAlfa = (color: string, porcentaje: number): string => `color-mix(in srgb, ${color} ${porcentaje}%, transparent)`;

/**
 * I-207 (BUG-2381) — el tema que la casa le ofrece a todo lo que cuelga de ella. La casa lo
 * provee UNA vez (`CasaDelProyecto`); una pieza compartida (`ui/Table`) lo lee sin que cada
 * consumidor tenga que pasárselo. Sin proveedor vale `undefined` ⇒ la pieza conserva su look
 * claro de siempre: las páginas fuera de la casa NO tienen modo oscuro.
 */
export const CasaTemaContext = createContext<CasaTema | undefined>(undefined);
export const useTemaDeLaCasa = (): CasaTema | undefined => useContext(CasaTemaContext);

/**
 * I-231 (Tarea 3) — UN solo resolver de color. «El tema que me toca»: el de la casa si hay proveedor,
 * y el CLARO si no — hoy sin proveedor `useTemaDeLaCasa()` devuelve `undefined` y cada pantalla se
 * inventaba su respaldo claro (`ui/Table` rgba propios, `M05/CreditosList` `casaTema(false)`,
 * `M01/IngresosList` `TEMA_CLARO` con `fg #111827` ≠ `#0F1419`). Una pieza que sólo necesita COLORES usa
 * este; `useTemaDeLaCasa()` queda para la pregunta «¿estoy dentro de la casa?» (elegir entre el look
 * de la casa y el clásico, como hacen `ui/Modal` y `ui/SlidePanel`).
 */
export const useTema = (): CasaTema => useContext(CasaTemaContext) ?? CLARO;

/**
 * D-2026-09-30-F (Mau, 30-sep «1B») — la escala de letra del sistema: 10 · 10.5 · 11 · 11.5 · 12 · 12.5 · 13 ·
 * 15 · 18 · 24. Son los tamaños que DIBUJÓ el diseño (el 11.5 es el nº 1 en 11 de 13 carpetas); la de 7
 * de D-2026-09-29-L no los traía. Lo que un sitio escribía fuera de la escala va al más cercano (empate: hacia arriba):
 * 9/9.5/8.5→10 · 13.5→13 · 14→15 · 16→15 · 17→18 · 19/20→18 · 21/22/26/28/30+→24.
 */
export const ESCALA_LETRA = [10, 10.5, 11, 11.5, 12, 12.5, 13, 15, 18, 24] as const;

/**
 * El tamaño se fija POR PIEZA, no pantalla por pantalla: un chip mide lo mismo en ingresos que en ventas.
 * Cada valor sale de la pantalla del diseño (`handoff_claude_ingresos_vestido/casa-ingresos-lista.jsx`,
 * `casa-ingresos-captura.jsx`, `casa-estados.jsx`, `handoff_claude_13_menu_lateral`), con su línea en
 * `qa/dia-2026-09-29/I231-TABLAS-PARA-MAU.md`. Escribir `fontSize: FS.chip`, nunca `fontSize: 10`.
 */
export const FS = {
  chip: 10, chipUnidad: 11,
  tituloTarjeta: 13, estado: 12.5, cuerpo: 11.5, lineaMono: 10.5, ayuda: 11,
  monto: 15, kpi: 18, kpiEtiqueta: 12, kpiNota: 11,
  seccion: 13, encabezadoTabla: 10.5, celda: 11.5,
  campo: 13, etiquetaCampo: 12.5,
  boton: 12, botonGrande: 12.5, pildora: 12,
  aviso: 11.5, avisoGrande: 12.5,
  tituloVacio: 18, cuerpoVacio: 13,
  modalTitulo: 18, modalTituloChico: 15,
  menuItem: 12.5, menuGrupo: 10.5, marcaNombre: 12.5,
  hero: 24,
} as const;

/** Cuando un sitio no es ninguna pieza nombrada (un ícono con texto, un número suelto): el paso de la escala, no un literal. */
export const PASO = { s10: 10, s10_5: 10.5, s11: 11, s11_5: 11.5, s12: 12, s12_5: 12.5, s13: 13, s15: 15, s18: 18, s24: 24 } as const;

/**
 * Los 4 estados REALES de un proyecto y su color del diseño (brief 13, README:87): activo → `accent`,
 * pausado → `warn` (el «Sin arrancar» del diseño), entregado → `fg3` (el «Cerrado»), cancelado → `danger`.
 * UNA sola respuesta para las tarjetas de la lista y el desplegable «Cambiar» (I-251). Un estado que
 * el servidor invente cae en `activo`, igual que antes (`ESTADO_CONFIG[...] ?? .activo`).
 */
export const ETIQUETA_DE_ESTADO: Record<string, string> = {
  activo: 'Activo', pausado: 'Pausado', entregado: 'Entregado', cancelado: 'Cancelado',
};
export function colorDeEstado(T: CasaTema, estado: string): string {
  switch (estado) {
    case 'pausado': return T.warn;
    case 'entregado': return T.fg3;
    case 'cancelado': return T.danger;
    default: return T.accent;
  }
}

const LLAVE = 'casa_modo';

/**
 * El modo de la casa. Arranca en OSCURO, que es como lo entregó Design y como lo pidió Mau.
 *
 * Se guarda en el navegador de cada quien: es una preferencia personal, no un dato del
 * negocio. Si el navegador no deja guardar (ventana privada, permisos), NO se rompe la
 * pantalla — se queda en oscuro y sigue.
 */
export function leerModoCasa(): boolean {
  try {
    const v = localStorage.getItem(LLAVE);
    return v === null ? true : v === 'oscuro';
  } catch { return true; }
}

const EVENTO_MODO = 'casa-modo-cambio';

export function guardarModoCasa(dark: boolean): void {
  try { localStorage.setItem(LLAVE, dark ? 'oscuro' : 'claro'); } catch { /* sin espacio: se queda en memoria */ }
  // I-242 — avisa al marco (menú, barra, paleta) para que cambie de modo junto con la casa, sin recargar.
  try { window.dispatchEvent(new Event(EVENTO_MODO)); } catch { /* sin window (pruebas de nodo) */ }
}

/**
 * I-242 (Tarea 2) — el modo del MARCO (menú lateral, barra de arriba, paleta ⌘K). El diseño
 * (`handoff_claude_13_menu_lateral/README.md`) lo dibuja en los dos modos con los tokens del tema.
 * A diferencia de la casa —que arranca en OSCURO—, el marco arranca en CLARO mientras nadie haya elegido
 * oscuro: el resto de las pantallas todavía es claro y un menú oscuro junto a una página clara sería un cambio
 * que nadie pidió. Cuando alguien elige oscuro en la casa, el marco lo sigue.
 */
export function leerModoDelMarco(): boolean {
  try { return localStorage.getItem(LLAVE) === 'oscuro'; } catch { return false; }
}

/** El tema del marco, reactivo: se actualiza cuando la casa guarda el modo (mismo navegador) o cambia otra pestaña. */
export function useTemaDelMarco(): CasaTema {
  const [dark, setDark] = useState<boolean>(leerModoDelMarco);
  useEffect(() => {
    const alCambiar = () => setDark(leerModoDelMarco());
    window.addEventListener(EVENTO_MODO, alCambiar);
    window.addEventListener('storage', alCambiar);
    return () => { window.removeEventListener(EVENTO_MODO, alCambiar); window.removeEventListener('storage', alCambiar); };
  }, []);
  // El marco FLOTA sobre páginas que todavía son claras: sus paneles translúcidos del oscuro (`rgba(255,255,255,0.045)`)
  // sólo se leen sobre el fondo oscuro de la casa; sobre una página clara el texto claro se vuelve invisible.
  // Por eso, en oscuro, el marco se pinta con el panel SÓLIDO del mismo tema.
  const T = casaTema(dark);
  return dark ? { ...T, panel: T.panelSolid } : T;
}

/**
 * Pesos en pantalla SIN centavos y SIN abreviar — el `mxnCompact` del diseño (`data-finanzas.jsx:44`):
 * tarjetas y KPIs grandes. 🔒 D-2026-09-22-J (BUG-2210): es una de las DOS formas de escribir un monto;
 * la otra es `formatearMonto()` (2 decimales, tablas y montos exactos). `$3.4M` no existe en el diseño.
 */
const MXN_LARGO = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
export function mxn(n: number | string | null | undefined): string {
  // Acepta texto como `formatearMonto`: un DECIMAL de MySQL llega como "1234.50" (BUG-2210 — los dos dueños leen igual).
  const v = typeof n === 'string' ? Number(n) : n;
  return MXN_LARGO.format(typeof v === 'number' && Number.isFinite(v) ? v : 0);
}
